import { z } from 'zod';
import { ROLES } from './enums.js';
/**
 * Роли и права.
 *
 * Роль строго одна на человека. Права проверяются и в интерфейсе, и на сервере —
 * скрытая кнопка не защита.
 */
export const PERMISSIONS = [
    /** Сдавать отчёты за себя */
    'reports:submit',
    /** Сдавать отчёты за любого сотрудника (с указанием причины) */
    'reports:submit:any',
    /** Видеть все отчёты, а не только свои */
    'reports:read:all',
    /** Корректировать сданные отчёты */
    'reports:correct',
    /** Принимать сданные отчёты: смена закрыта только после подтверждения */
    'reports:accept',
    /** Справочники: участки, агрегаты, сырьё, рецептура, нормы */
    'directories:manage',
    /** Сотрудники: заведение, пароли, допуски */
    'users:manage',
    /** Решение об увольнении и восстановлении. Администратор только предлагает */
    'staff:dismiss',
    /** График смен: сбор пожеланий, распределение, публикация */
    'schedule:manage',
    /** Свои пожелания и заявки на отсутствие */
    'schedule:own',
    /** Склад: первичные остатки и инвентаризация */
    'stock:manage',
    /** Приёмка сырья от поставщика */
    'stock:receive',
    /** Отгрузка продукции покупателю и возвраты */
    'stock:ship',
    /** Найм: кандидаты, воронка, реестр неявок */
    'candidates:manage',
    /** Отметка явки на экскурсию и стажировку */
    'candidates:attendance',
    /** Аналитика и выгрузки */
    'analytics:read',
];
export const ROLE_PERMISSIONS = {
    OPERATOR: ['reports:submit', 'schedule:own'],
    /**
     * Мастер участка ведёт свой участок: принимает смены, правит ошибки в цифрах
     * и ставит людей в график.
     *
     * Право одно на всех мастеров, а участки закреплены за человеком тем же
     * механизмом допусков, что и у операторов. Заводить отдельную роль под каждый
     * участок значило бы менять код всякий раз, когда появляется новый мастер.
     */
    MASTER: [
        'reports:read:all',
        'reports:accept',
        'reports:correct',
        'schedule:manage',
        'schedule:own',
        'analytics:read',
        // Сырьё принимает тот, кто стоит у машины: мастер подготовки сырья.
        // Право одно на всех мастеров — на прессовании его просто не применяют.
        'stock:receive',
    ],
    ADMIN: [
        'reports:submit',
        'reports:submit:any',
        'reports:read:all',
        'reports:correct',
        'reports:accept',
        'directories:manage',
        'users:manage',
        'schedule:manage',
        'schedule:own',
        'stock:manage',
        'stock:receive',
        'stock:ship',
        'candidates:attendance',
        'analytics:read',
    ],
    HR: ['candidates:manage', 'candidates:attendance', 'analytics:read'],
    // Руководитель смотрит производство и ведёт то, что задаёт правила работы:
    // штат, справочники сырья, рецептуру и остатки. В сменные отчёты не вмешивается.
    MANAGER: [
        'reports:read:all',
        'analytics:read',
        'users:manage',
        // Увольняет и восстанавливает только руководитель. Администратор увольнение
        // предлагает, но решение за ним не остаётся — иначе он мог бы убрать из
        // системы и HR, и самого руководителя.
        'staff:dismiss',
        'directories:manage',
        'stock:manage',
        // Руководитель может и принять, и отгрузить: он отвечает за материал целиком
        'stock:receive',
        'stock:ship',
    ],
};
export function can(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
/**
 * Кто может СМОТРЕТЬ график смен, отсутствия и пожелания: администратор, который
 * его ведёт, и руководитель, которому нужна вся картина по производству.
 * HR сюда не попадает — в цеховые дела он не лезет.
 *
 * Менять график по-прежнему может только администратор: изменения идут
 * событиями, и права на них проверяются при приёме события.
 */
export const SCHEDULE_READ_PERMISSIONS = ['schedule:manage', 'reports:read:all'];
export function canReadSchedule(role) {
    return SCHEDULE_READ_PERMISSIONS.some((permission) => can(role, permission));
}
export function isRole(value) {
    return ROLES.includes(value);
}
/**
 * Сроки сессий из ТЗ: оператору неудобно вводить пароль каждую неделю у станка,
 * а у административных учётных записей доступ шире — срок короче.
 */
export const SESSION_TTL_DAYS = {
    OPERATOR: 30,
    STAFF: 7,
};
/** Канонический вид логина. Один и тот же на клиенте и на сервере. */
export function normalizeLogin(login) {
    return login.trim().toLowerCase();
}
/**
 * Логин сравнивается без учёта регистра: приводим к нижнему и здесь, и при
 * заведении сотрудника. В цеху вход набирают на телефоне, где заглавную букву
 * надо ставить намеренно, а ошибка выглядит как «неверный пароль» — и после
 * пяти попыток человек оказывается заблокирован, так и не поняв причины.
 */
export const passwordLoginSchema = z.object({
    login: z.string().trim().toLowerCase().min(2).max(64),
    password: z.string().min(8).max(200),
});
export function authUserFrom(user) {
    return {
        id: user.id,
        fullName: user.fullName,
        role: user.role,
        permissions: ROLE_PERMISSIONS[user.role],
    };
}
//# sourceMappingURL=auth.js.map